// ORBAT-seed: hele Forsvarets styrkestruktur under rot «FOH», med fornuftige
// APP-6/2525 SIDC-er per node. Dette er default-treet ORBAT-creatoren laster
// (superadmin/FOH ser hele). Bygget som ren data + byggere slik at det kan
// testes for integritet (alle 11 HV-distrikter, alle innsatsstyrker, gyldige
// stier). Strukturen er ordre-de-bataille på FORMASJONSNIVÅ — ikke en fasit på
// oppsatt styrke; sjefer bygger detaljene videre i creatoren.
//
// Kilder: Forsvarets offentlige organisasjonsoversikt + HV-strukturen i
// hv-structure.ts (distrikter/områder). Navnene er reelle; SIDC-ene er
// representative (venn, riktig dimensjon + echelon).
import { OrbatUnitSchema, ORBAT_ECHELON_SIDC, withOrbatPaths, } from './orbat.js';
import { HV_DISTRICTS, districtAreas } from './hv-structure.js';
/** Bygg en 15-tegns venn-SIDC fra funksjon + echelon + kampdimensjon. */
function sidc(func, ech, dim = 'G') {
    const f = (func + '------').slice(0, 6);
    return ('S' + 'F' + dim + 'P' + f + '-' + ORBAT_ECHELON_SIDC[ech] + '---').slice(0, 15);
}
/** Innsatsstyrke per HV-distrikt (task-spesifisert standkvarter + I-styrke). */
const HV_SEED = [
    { id: 'HV-01', name: 'HV-01 Oslofjord', hq: 'Rygge', ist: ['Polar Bear VI'] },
    { id: 'HV-02', name: 'HV-02 Oslo og Akershus', hq: 'Lutvann', ist: ['Derby'] },
    { id: 'HV-03', name: 'HV-03 Telemark og Buskerud', hq: 'Heistadmoen', ist: ['Gunnerside'] },
    { id: 'HV-05', name: 'HV-05 Opplandske', hq: 'Terningmoen', ist: ['Grebe'] },
    { id: 'HV-08', name: 'HV-08 Agder og Rogaland', hq: 'Vatneleiren', ist: ['Osprey', 'Varg'] },
    { id: 'HV-09', name: 'HV-09 Bergenhus', hq: 'Bergenhus', ist: ['Bjørn West'] },
    { id: 'HV-11', name: 'HV-11 Møre og Fjordane', hq: 'Setnesmoen', ist: ['Archery'] },
    { id: 'HV-12', name: 'HV-12 Trøndelag', hq: 'Værnes', ist: ['Rype'] },
    { id: 'HV-14', name: 'HV-14 Sør-Hålogaland', hq: 'Drevjamoen', ist: ['Heron'] },
    { id: 'HV-16', name: 'HV-16 Nord-Hålogaland', hq: 'Elvegårdsmoen', ist: ['Claymore'] },
    { id: 'HV-17', name: 'HV-17 Finnmark', hq: 'Porsangmoen', ist: ['Ida & Lyra'] },
];
/** Bygg hele ORBAT-seeden (flat liste, med materialiserte stier). */
export function buildOrbatSeed() {
    const U = [];
    const mk = (id, name, echelon, parentId, o = {}) => {
        U.push(OrbatUnitSchema.parse({
            id, name, echelon, parentId,
            sidc: o.sidc ?? sidc(o.func ?? 'UH', echelon, o.dim ?? 'G'),
            hq: o.hq, note: o.note, authorized: o.authorized ?? 0,
        }));
    };
    // ── Rot: FOH ───────────────────────────────────────────────────────────────
    mk('FOH', 'Forsvarets operative hovedkvarter', 'foh', undefined, { func: 'UH', hq: 'Reitan' });
    // ── Hæren ────────────────────────────────────────────────────────────────
    mk('Haeren', 'Hæren', 'forsvarsgren', 'FOH', { func: 'UCI' });
    mk('BrigNord', 'Brigade Nord', 'brigade', 'Haeren', { func: 'UCI', hq: 'Bardufoss' });
    mk('Panserbn', 'Panserbataljonen', 'bataljon', 'BrigNord', { func: 'UCA' });
    mk('Bn2', '2. bataljon', 'bataljon', 'BrigNord', { func: 'UCI' });
    mk('TmBn', 'Telemark bataljon', 'bataljon', 'BrigNord', { func: 'UCIZ' });
    mk('ArtBn', 'Artilleribataljonen', 'bataljon', 'BrigNord', { func: 'UCF' });
    mk('SanBn', 'Sanitetsbataljonen', 'bataljon', 'BrigNord', { func: 'UCM' });
    mk('SambBn', 'Sambandsbataljonen', 'bataljon', 'BrigNord', { func: 'UUS' });
    mk('IngBn', 'Ingeniørbataljonen', 'bataljon', 'BrigNord', { func: 'UCE' });
    mk('StridstrenBn', 'Stridstrenbataljonen', 'bataljon', 'BrigNord', { func: 'USS' });
    mk('MpKp', 'Militærpolitikompaniet', 'kompani', 'BrigNord', { func: 'UUL' });
    mk('Finnmark', 'Finnmark landforsvar', 'brigade', 'Haeren', { func: 'UCI', hq: 'Porsangmoen' });
    mk('PorsangerBn', 'Porsanger bataljon', 'bataljon', 'Finnmark', { func: 'UCI' });
    mk('GSV', 'GSV / Jegerbataljonen', 'bataljon', 'Finnmark', { func: 'UCR' });
    mk('HMKG', 'Hans Majestet Kongens Garde', 'bataljon', 'Haeren', { func: 'UCI', hq: 'Huseby' });
    mk('HVS', 'Hærens våpenskole', 'bataljon', 'Haeren', { func: 'UH', hq: 'Rena' });
    // ── Sjøforsvaret ─────────────────────────────────────────────────────────
    mk('Sjoforsvaret', 'Sjøforsvaret', 'forsvarsgren', 'FOH', { func: 'UH', dim: 'S' });
    mk('Marinen', 'Marinen', 'brigade', 'Sjoforsvaret', { func: 'UH', dim: 'S', hq: 'Haakonsvern' });
    mk('Fregattvapenet', 'Fregattvåpenet', 'bataljon', 'Marinen', { dim: 'S' });
    mk('Ubatvapenet', 'Ubåtvåpenet', 'bataljon', 'Marinen', { dim: 'S' });
    mk('Korvettvapenet', 'Korvettvåpenet', 'bataljon', 'Marinen', { dim: 'S' });
    mk('Minerydder', 'Mineryddervåpenet', 'bataljon', 'Marinen', { dim: 'S' });
    mk('MarLog', 'Marinens logistikkvåpen', 'bataljon', 'Marinen', { func: 'USS', dim: 'S' });
    mk('KJK', 'Kystjegerkommandoen', 'bataljon', 'Marinen', { func: 'UCR', dim: 'G' });
    mk('Kystvakten', 'Kystvakten', 'brigade', 'Sjoforsvaret', { func: 'UH', dim: 'S', hq: 'Sortland' });
    mk('YtreKV', 'Ytre kystvakt', 'bataljon', 'Kystvakten', { dim: 'S' });
    mk('IndreKV', 'Indre kystvakt', 'bataljon', 'Kystvakten', { dim: 'S' });
    // ── Luftforsvaret ──────────────────────────────────────────────────────────
    mk('Luftforsvaret', 'Luftforsvaret', 'forsvarsgren', 'FOH', { func: 'UH', dim: 'A' });
    mk('Lv131', '131 luftving', 'brigade', 'Luftforsvaret', { func: 'UH', dim: 'A', hq: 'Sørreisa' });
    mk('Lv132', '132 luftving', 'brigade', 'Luftforsvaret', { func: 'UH', dim: 'A', hq: 'Ørland' });
    mk('Skv332', '332 skvadron (F-35)', 'bataljon', 'Lv132', { dim: 'A' });
    mk('Lv133', '133 luftving', 'brigade', 'Luftforsvaret', { func: 'UH', dim: 'A', hq: 'Evenes' });
    mk('Skv333', '333 skvadron (P-8)', 'bataljon', 'Lv133', { dim: 'A' });
    mk('Skv339', '339 skvadron (Bell 412)', 'bataljon', 'Lv133', { dim: 'A' });
    mk('Lv134', '134 luftving', 'brigade', 'Luftforsvaret', { func: 'UH', dim: 'A', hq: 'Sørreisa' });
    mk('Lv135', '135 luftving', 'brigade', 'Luftforsvaret', { func: 'UH', dim: 'A', hq: 'Gardermoen' });
    mk('Skv335', '335 skvadron (C-130J)', 'bataljon', 'Lv135', { dim: 'A' });
    mk('Skv337', '337 skvadron (NH90)', 'bataljon', 'Lv135', { dim: 'A' });
    mk('Skv330', '330 skvadron (SAR, AW101)', 'bataljon', 'Luftforsvaret', { dim: 'A', hq: 'Sola' });
    // ── Forsvarets spesialstyrker ──────────────────────────────────────────────
    mk('Spesialstyrker', 'Forsvarets spesialstyrker', 'forsvarsgren', 'FOH', { func: 'UCR' });
    mk('FSK', 'Forsvarets spesialkommando', 'bataljon', 'Spesialstyrker', { func: 'UCR', hq: 'Rena' });
    mk('MJK', 'Marinejegerkommandoen', 'bataljon', 'Spesialstyrker', { func: 'UCR', hq: 'Haakonsvern' });
    // ── Heimevernet ────────────────────────────────────────────────────────────
    mk('Heimevernet', 'Heimevernet', 'forsvarsgren', 'FOH', { func: 'UCI', hq: 'Terningmoen' });
    for (const d of HV_SEED) {
        mk(d.id, d.name, 'distrikt', 'Heimevernet', { func: 'UCI', hq: d.hq });
        // Områdestruktur: distriktets tradisjonelle HV-områder (områdesjef = kompani).
        const omrId = `${d.id}.omr`;
        mk(omrId, 'Områdestruktur', 'bataljon', d.id, { func: 'UCI' });
        const src = HV_DISTRICTS.find((x) => x.id === d.id);
        if (src) {
            for (const area of districtAreas(src)) {
                mk(area.id, area.name, 'kompani', omrId, {
                    func: 'UCI',
                    note: area.municipalities.length ? `Kommuner: ${area.municipalities.join(', ')}` : undefined,
                });
            }
        }
        // Innsatsstyrke(r) (I-styrke) direkte under distriktet.
        if (d.ist.length === 1) {
            mk(`${d.id}.ist`, `Innsatsstyrke ${d.ist[0]}`, 'kompani', d.id, { func: 'UCR' });
        }
        else {
            let n = 0;
            for (const s of d.ist)
                mk(`${d.id}.ist${n++}`, `Innsatsstyrke ${s}`, 'kompani', d.id, { func: 'UCR' });
        }
    }
    // ── Felleskapasiteter ──────────────────────────────────────────────────────
    mk('Felles', 'Felleskapasiteter', 'forsvarsgren', 'FOH', { func: 'UH' });
    mk('Cyber', 'Cyberforsvaret', 'brigade', 'Felles', { func: 'UUS', hq: 'Jørstadmoen' });
    mk('Etjenesten', 'Etterretningstjenesten', 'brigade', 'Felles', { func: 'UUM', hq: 'Lutvann' });
    mk('FLO', 'Forsvarets logistikkorganisasjon', 'brigade', 'Felles', { func: 'USS', hq: 'Bergen' });
    mk('FSAN', 'Forsvarets sanitet', 'brigade', 'Felles', { func: 'UCM', hq: 'Sessvollmoen' });
    // ── SKANDIA: Sverige (Hemvärnet) ── egen rot, sidestilt med FOH ─────────────
    buildSweden(mk);
    // ── SKANDIA: Danmark (Hjemmeværnet) ── egen rot ────────────────────────────
    buildDenmark(mk);
    return withOrbatPaths(U);
}
/** Sveriges Hemvärn: Rikshemvärnsstaben → 4 militärregioner + Gotland → 40 bataljoner. */
function buildSweden(mk) {
    mk('SWE', 'Sverige — Hemvärnet', 'foh', undefined, { func: 'UH', hq: 'Rikshemvärnsstaben' });
    mk('SWE.HvSS', 'Hemvärnets stridsskola (HvSS)', 'brigade', 'SWE', { func: 'UH', hq: 'Södertälje' });
    // Militärregion → [nr, navn, stab, gruppe]
    const MR = [
        { id: 'MRN', name: 'Norra militärregionen', hq: 'Boden', bn: [
                [10, 'Lapplandsjägarbataljonen', 'Kiruna', 'Lapplandsjägargruppen'],
                [11, 'Gränsjägarbataljonen', 'Kiruna', 'Lapplandsjägargruppen'],
                [12, 'Norrbottensbataljonen', 'Boden', 'Norrbottensgruppen'],
                [13, 'Västerbottensbataljonen', 'Umeå', 'Västerbottensgruppen'],
                [14, 'Fältjägarbataljonen', 'Östersund', 'Fältjägargruppen'],
                [15, 'Ångermanlandsbataljonen', 'Härnösand', 'Västernorrlandsgruppen'],
                [16, 'Medelpadsbataljonen', 'Härnösand', 'Västernorrlandsgruppen']
            ] },
        { id: 'MRM', name: 'Mellersta militärregionen', hq: 'Kungsängen', bn: [
                [17, 'Dalabataljonen', 'Falun', 'Dalregementsgruppen'],
                [18, 'Gävleborgsbataljonen', 'Gävle', 'Gävleborgsgruppen'],
                [21, 'Upplandsbataljonen', 'Enköping', 'Upplands- och Västmanlandsgruppen'],
                [22, 'Västmanlandsbataljonen', 'Enköping', 'Upplands- och Västmanlandsgruppen'],
                [23, 'Attundalandsbataljonen', 'Kungsängen', 'Livgardesgruppen'],
                [24, 'Stockholmsbataljonen', 'Kungsängen', 'Livgardesgruppen'],
                [25, 'Telgehusbataljonen', 'Kungsängen', 'Livgardesgruppen'],
                [26, 'Ulvsundabataljonen', 'Kungsängen', 'Livgardesgruppen'],
                [27, 'Södermanlandsbataljonen', 'Strängnäs', 'Södermanlandsgruppen'],
                [28, 'Roslagsbataljonen', 'Haninge', 'Södertörnsgruppen'],
                [29, 'Södertörnsbataljonen', 'Haninge', 'Södertörnsgruppen']
            ] },
        { id: 'MRV', name: 'Västra militärregionen', hq: 'Skövde', bn: [
                [19, 'Värmlandsbataljonen', 'Karlstad', 'Örebro-Värmlandsgruppen'],
                [20, 'Sannahedsbataljonen', 'Örebro', 'Örebro-Värmlandsgruppen'],
                [38, 'Kinnebataljonen', 'Skövde', 'Skaraborgsgruppen'],
                [39, 'Kåkindbataljonen', 'Skövde', 'Skaraborgsgruppen'],
                [40, 'Bohusbataljonen', 'Skredsvik', 'Bohusdalgruppen'],
                [41, 'Göteborgs södra bataljon', 'Göteborg', 'Elfsborgsgruppen'],
                [42, 'Göteborgs norra bataljon', 'Göteborg', 'Elfsborgsgruppen'],
                [43, 'Göteborgs skärgårdsbataljon', 'Göteborg', 'Elfsborgsgruppen'],
                [44, 'Älvsborgsbataljonen', 'Göteborg', 'Elfsborgsgruppen'],
                [45, 'Hallandsbataljonen', 'Halmstad', 'Hallandsgruppen']
            ] },
        { id: 'MRS', name: 'Södra militärregionen', hq: 'Revingehed', bn: [
                [30, '1. Livgrenadjärbataljonen', 'Linköping', 'Livgrenadjärgruppen'],
                [31, '2. Livgrenadjärbataljonen', 'Linköping', 'Livgrenadjärgruppen'],
                [33, 'Norra Smålandsbataljonen', 'Eksjö', 'Norra Smålandsgruppen'],
                [34, 'Kalmarbataljonen', 'Kalmar', 'Kalmar- och Kronobergsgruppen'],
                [35, 'Kronobergsbataljonen', 'Växjö', 'Kalmar- och Kronobergsgruppen'],
                [36, 'Blekinge västra bataljon', 'Karlskrona', 'Blekingegruppen'],
                [37, 'Blekinge östra bataljon', 'Karlskrona', 'Blekingegruppen'],
                [46, 'Södra skånska bataljonen', 'Revingehed', 'Skånska gruppen'],
                [47, 'Malmöhusbataljonen', 'Malmö', 'Skånska gruppen'],
                [48, 'Skånska dragonbataljonen', 'Helsingborg', 'Skånska gruppen'],
                [49, 'Norra skånska bataljonen', 'Rinkaby', 'Skånska gruppen']
            ] },
    ];
    for (const mr of MR) {
        mk(`SWE.${mr.id}`, mr.name, 'distrikt', 'SWE', { func: 'UCI', hq: mr.hq });
        for (const [nr, name, stab, grp] of mr.bn) {
            mk(`SWE.hvbat${nr}`, `${nr}. ${name}`, 'bataljon', `SWE.${mr.id}`, { func: 'UCI', hq: stab, note: grp });
        }
    }
    // Gotland: 32. bataljon direkte under Gotlands regemente (P 18) — frontlinje.
    mk('SWE.P18', 'Gotlands regemente (P 18)', 'brigade', 'SWE', { func: 'UCA', hq: 'Visby' });
    mk('SWE.hvbat32', '32. Gotlandsbataljonen', 'bataljon', 'SWE.P18', { func: 'UCI', hq: 'Visby', note: 'Gotlands regemente (P 18)' });
}
/** Danmarks Hjemmeværn: HJK → Hær- (2 landsdelsregioner × distrikter) + Marine- + Flyverhjemmeværn. */
function buildDenmark(mk) {
    mk('DNK', 'Danmark — Hjemmeværnet', 'foh', undefined, { func: 'UH', hq: 'Hjemmeværnskommandoen, Vordingborg' });
    // Hærhjemmeværnet
    mk('DNK.Haer', 'Hærhjemmeværnet', 'forsvarsgren', 'DNK', { func: 'UCI' });
    const west = [
        ['Nordjylland', 'Nordjylland', 'Nørresundby'],
        ['MidtVestjylland', 'Midt- og Vestjylland', 'Skive'],
        ['Ostjylland', 'Østjylland', 'Brabrand (Aarhus)'],
        ['Sydostjylland', 'Sydøstjylland', 'Vejle'],
        ['SydSonderjylland', 'Syd- og Sønderjylland', 'Søgaard'],
        ['Fyn', 'Fyn', 'Odense'],
    ];
    const east = [
        ['Kobenhavn', 'København', 'Svanemøllens Kaserne'],
        ['KobenhavnVestegn', 'Københavns Vestegn', 'Jonstruplejren (Ballerup)'],
        ['Nordsjaelland', 'Nordsjælland', 'Fredensborg'],
        ['MidtVestsjaelland', 'Midt- og Vestsjælland', 'Flyvestation Skalstrup'],
        ['SydsjaellandLF', 'Sydsjælland og Lolland-Falster', 'Vordingborg Kaserne'],
        ['Bornholm', 'Det Bornholmske Hjemmeværn', 'Almegårds Kaserne (Rønne)'],
    ];
    mk('DNK.LRGVest', 'Landsdelsregion Vest', 'distrikt', 'DNK.Haer', { func: 'UCI', hq: 'Skive' });
    for (const [id, name, hq] of west)
        mk(`DNK.${id}`, name, 'bataljon', 'DNK.LRGVest', { func: 'UCI', hq });
    mk('DNK.LRGOst', 'Landsdelsregion Øst', 'distrikt', 'DNK.Haer', { func: 'UCI', hq: 'Kastellet, København' });
    for (const [id, name, hq] of east)
        mk(`DNK.${id}`, name, 'bataljon', 'DNK.LRGOst', { func: 'UCI', hq });
    // Marinehjemmeværnet (~38 flotiljer + MFP) — gren + representative flotiljer.
    mk('DNK.Marine', 'Marinehjemmeværnet', 'forsvarsgren', 'DNK', { func: 'UH', dim: 'S', note: '~38 flotiljer, ~30 fartøy (MHV 800/900) + MFP' });
    for (const [id, name, hq] of [
        ['HVF115', 'HVF 115 Vendsyssel', 'Frederikshavn'], ['HVF124', 'HVF 124', 'Århus'],
        ['HVF200', 'HVF 200 (MFP)', 'Marinestation Holmen'], ['HVF255', 'HVF 255', 'Korsør'],
        ['HVF369', 'HVF 369', 'Holmen (København)'],
    ])
        mk(`DNK.${id}`, name, 'kompani', 'DNK.Marine', { func: 'UH', dim: 'S', hq });
    // Flyverhjemmeværnet (~28 eskadriller, STO) — gren + representative eskadriller.
    mk('DNK.Flyver', 'Flyverhjemmeværnet', 'forsvarsgren', 'DNK', { func: 'UH', dim: 'A', note: '~28 eskadriller (Survive-To-Operate)' });
    for (const [id, name, hq] of [
        ['HVE266', 'HVE 266', 'Aalborg'], ['HVE270', 'HVE 270 Flyvende eskadrille (ISR)', 'Aalborg/Roskilde'],
        ['HVE277', 'HVE 277', 'Karup'], ['HVE284', 'HVE 284', 'Skrydstrup (F-35)'],
    ])
        mk(`DNK.${id}`, name, 'kompani', 'DNK.Flyver', { func: 'UH', dim: 'A', hq });
}
/** Ferdig materialisert seed (default-treet for ORBAT-creatoren). */
export const ORBAT_SEED = buildOrbatSeed();
//# sourceMappingURL=orbat-seed.js.map